import { useState, useEffect } from 'react';
import { axiosInstance } from '../lib/api-client';
import { X, Search, Gift, DollarSign, Calendar, User, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { logger } from '../utils/logger';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface Plan {
  _id: string;
  name: string;
  price: number;
  duration: number;
  contactsAllowed: number;
}

interface Settings {
  maxDiscountPercentages: number[];
  maxExtraContacts: number[];
  maxExtraValidity: number[];
  commissionPercentage: number;
}

interface CreatePromotionalOrderFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function CreatePromotionalOrderForm({ isOpen, onClose, onSuccess }: CreatePromotionalOrderFormProps) {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchError, setSearchError] = useState<string>('');
  
  const [formData, setFormData] = useState({
    planId: '',
    discountPercentage: 0,
    extraContacts: 0,
    extraValidity: 0,
    durationInMinutes: 1440, // Default 1 day = 1440 minutes
    campaignName: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
      fetchSettings();
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    try {
      const response = await axiosInstance.get('/admin/plans');
      
      if (response.data.success) {
        // Handle both response formats: data.plans or data directly
        const plansData = response.data.data?.plans || response.data.data || [];
        const plansArray = Array.isArray(plansData) ? plansData : [];
        setPlans(plansArray);
        logger.info('Plans loaded:', plansArray);
      }
    } catch (error: any) {
      logger.error('Error fetching plans:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch plans');
      setPlans([]); // Set empty array on error
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/promotional-orders/settings');
      
      if (response.data.success) {
        setSettings(response.data.data);
        if (response.data.data.maxDiscountPercentages.length > 0) {
          setFormData(prev => ({
            ...prev,
            discountPercentage: response.data.data.maxDiscountPercentages[0]
          }));
        }
      }
    } catch (error: any) {
      logger.error('Error fetching settings:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch settings');
    }
  };

  const handleSearchUser = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(searchEmail.trim())) {
      setSearchError('Please enter a valid email address');
      return;
    }

    setSearchError('');
    
    try {
      setSearching(true);
      setSearchResults([]);
      
      const response = await axiosInstance.get(
        `/admin/users/search?q=${encodeURIComponent(searchEmail.trim())}`
      );

      if (response.data.success) {
        const users = response.data.data || [];
        
        // Handle different response formats from backend
        // Backend can return: null, single object, or array
        let usersArray: User[] = [];
        
        if (users === null) {
          // No user found
          usersArray = [];
        } else if (Array.isArray(users)) {
          // Already an array
          usersArray = users;
        } else if (typeof users === 'object') {
          // Single user object - convert to array
          // Extract userId if it's a profile object
          const userObj = users.userId || users;
          usersArray = [userObj];
        }
        
        // Filter to exact email match only
        const exactMatch = usersArray.filter((user: User) => 
          user.email.toLowerCase() === searchEmail.trim().toLowerCase()
        );
        
        if (exactMatch.length === 0) {
          setSearchError('No user found with this email address');
          setSearchResults([]);
        } else {
          setSearchResults(exactMatch);
          setSearchError('');
        }
      }
    } catch (error: any) {
      logger.error('Error searching users:', error);
      setSearchError(error.response?.data?.message || 'Failed to search user');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchUser();
    }
  };

  const selectUser = (user: User) => {
    setSelectedUser(user);
    setSearchEmail('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    if (!formData.planId) {
      toast.error('Please select a plan');
      return;
    }

    const selectedPlan = plans.find(p => p._id === formData.planId);
    if (!selectedPlan) {
      toast.error('Invalid plan selected');
      return;
    }

    const discountedPrice = selectedPlan.price * (1 - formData.discountPercentage / 100);

    try {
      setLoading(true);
      
      const response = await axiosInstance.post(
        '/promotional-orders',
        {
          userId: selectedUser._id,
          planId: formData.planId,
          discountedPrice,
          extraContacts: formData.extraContacts,
          extraValidity: formData.extraValidity,
          durationInMinutes: formData.durationInMinutes,
          campaignName: formData.campaignName,
          notes: formData.notes
        }
      );

      if (response.data.success) {
        toast.success('Promotional order created successfully!');
        onSuccess?.();
        handleClose();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create promotional order');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSearchEmail('');
    setSearchResults([]);
    setFormData({
      planId: '',
      discountPercentage: settings?.maxDiscountPercentages?.[0] || 0,
      extraContacts: 0,
      extraValidity: 0,
      durationInMinutes: 1440, // Default 1 day = 1440 minutes
      campaignName: '',
      notes: ''
    });
    onClose();
  };

  const selectedPlan = plans.find(p => p._id === formData.planId);
  const originalPrice = selectedPlan?.price || 0;
  const discountedPrice = originalPrice * (1 - formData.discountPercentage / 100);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-rose-500" />
            Create Promotional Order
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Search */}
          <div className="space-y-2">
            <Label>Select User *</Label>
            {selectedUser ? (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </p>
                    <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedUser(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="Enter exact user email address..."
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      onKeyPress={handleEmailKeyPress}
                      className="pl-10"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleSearchUser}
                    disabled={searching || !searchEmail}
                    className="bg-rose-500 hover:bg-rose-600"
                  >
                    {searching ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search
                      </>
                    )}
                  </Button>
                </div>
                
                {searchError && (
                  <p className="text-sm text-red-500">{searchError}</p>
                )}
                
                {searchResults.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {searchResults.map((user) => (
                      <button
                        key={user._id}
                        type="button"
                        onClick={() => selectUser(user)}
                        className="w-full p-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <p className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <p className="text-sm text-gray-500">{user.phone}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Plan Selection */}
          <div className="space-y-2">
            <Label>Select Plan *</Label>
            <Select
              value={formData.planId}
              onValueChange={(value) => setFormData({ ...formData, planId: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a plan..." />
              </SelectTrigger>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan._id} value={plan._id}>
                    {plan.name} - ₹{plan.price} ({plan.duration} days, {plan.contactsAllowed} contacts)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Discount & Extras */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Discount Percentage *</Label>
              <Select
                value={formData.discountPercentage.toString()}
                onValueChange={(value) => setFormData({ ...formData, discountPercentage: Number(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select discount..." />
                </SelectTrigger>
                <SelectContent>
                  {settings?.maxDiscountPercentages.map((discount) => (
                    <SelectItem key={discount} value={discount.toString()}>
                      {discount}% off
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Offer Valid For (Minutes) *</Label>
              <Input
                type="number"
                min="1"
                max="43200"
                placeholder="e.g., 1440 for 1 day, 10080 for 1 week"
                value={formData.durationInMinutes}
                onChange={(e) => setFormData({ ...formData, durationInMinutes: Number(e.target.value) })}
              />
              <p className="text-xs text-gray-500">
                {formData.durationInMinutes >= 1440 
                  ? `≈ ${(formData.durationInMinutes / 1440).toFixed(1)} days` 
                  : `${formData.durationInMinutes} minutes`}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Extra Contacts (Bonus)</Label>
              <Select
                value={formData.extraContacts.toString()}
                onValueChange={(value) => setFormData({ ...formData, extraContacts: Number(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contacts..." />
                </SelectTrigger>
                <SelectContent>
                  {settings?.maxExtraContacts.map((contacts) => (
                    <SelectItem key={contacts} value={contacts.toString()}>
                      {contacts === 0 ? 'No extra contacts' : `+${contacts} contacts`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Extra Validity Days (Bonus)</Label>
              <Select
                value={formData.extraValidity.toString()}
                onValueChange={(value) => setFormData({ ...formData, extraValidity: Number(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select validity..." />
                </SelectTrigger>
                <SelectContent>
                  {settings?.maxExtraValidity.map((days) => (
                    <SelectItem key={days} value={days.toString()}>
                      {days === 0 ? 'No extra validity' : `+${days} days`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Campaign Info */}
          <div className="space-y-2">
            <Label>Campaign Name (Optional)</Label>
            <Input
              type="text"
              placeholder="e.g., Summer Sale 2024"
              value={formData.campaignName}
              onChange={(e) => setFormData({ ...formData, campaignName: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              placeholder="Add any notes about this promotional offer..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          {/* Price Summary */}
          {selectedPlan && (
            <div className="p-4 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-rose-500" />
                Price Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Original Price:</span>
                  <span className="font-medium line-through text-gray-400">₹{originalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount ({formData.discountPercentage}%):</span>
                  <span className="font-medium text-green-600">-₹{(originalPrice - discountedPrice).toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-rose-200">
                  <span className="font-semibold text-gray-900">Final Price:</span>
                  <span className="text-xl font-bold text-rose-600">₹{discountedPrice.toFixed(2)}</span>
                </div>
                {formData.extraContacts > 0 && (
                  <div className="flex items-center gap-2 text-green-600 pt-2">
                    <Zap className="w-4 h-4" />
                    <span>Bonus: +{formData.extraContacts} contacts</span>
                  </div>
                )}
                {formData.extraValidity > 0 && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <Calendar className="w-4 h-4" />
                    <span>Bonus: +{formData.extraValidity} days validity</span>
                  </div>
                )}
                {settings && (
                  <div className="flex items-center gap-2 text-orange-600">
                    <DollarSign className="w-4 h-4" />
                    <span>Your Commission: ₹{(discountedPrice * settings.commissionPercentage / 100).toFixed(2)} ({settings.commissionPercentage}%)</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedUser || !formData.planId || formData.discountPercentage === 0}
              className="bg-rose-500 hover:bg-rose-600"
            >
              {loading ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Create Promotional Order
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}